// reporteTaxidermia.pdfmake.js
// Reporte mensual de Taxidermia: ventas/insumos, comodatos, cobros, gastos,
// actividades por etapa (Montaje/Show Room/Almacén) y cuentas por cobrar.
// Estilo "presentación" (slides): banda de título a color por sección + tarjetas
// de KPI en la portada, sobre el sistema de diseño de marca v2.

import moment from "moment";
import "moment/locale/es";
import {
  PDF_COLORS,
  pdfStylesV2,
  applyPdfColors,
  pdfFz,
} from "../Components/PdfComponents/componentsv2.0/stylesPDFv2";
import { PDFHeaderV2 } from "../Components/PdfComponents/componentsv2.0/PDFHeader";
import { PDFBrandFooterV2 } from "../Components/PdfComponents/componentsv2.0/PDFFooter";
import MULISH_FONT from "./PdfV2.0/mulishFont";
import "./PdfV2.0/mulishSemiBoldFont";

moment.locale("es");

const PAGE_MARGINS = [28, 70, 28, 40];

// Pixel transparente 1x1: respaldo válido para pdfmake cuando la empresa no
// trae logo (evita que "image: logo" reviente la generación del PDF).
const BLANK_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";

// Helpers
const money = (n) => {
  const x = Number(n || 0);
  return x.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
};

const fmtDate = (d) => {
  if (!d) return "";
  return moment(d).locale("es").format("L");
};

const estadoActividadTxt = (estadoActividad) => {
  // según tu lógica: 2=En proceso (sin fin), 3=Finalizada (con fin)
  if (estadoActividad === 3) return "Finalizada";
  if (estadoActividad === 2) return "En proceso";
  return String(estadoActividad || "");
};

// ===== Estilo "slide": banda de título a color de ancho completo =====
const slideTitleBand = (titulo, subtitulo) => ({
  margin: [0, 0, 0, 14],
  table: {
    widths: ["*"],
    body: [[
      {
        stack: [
          { text: titulo, style: "slideTitle" },
          ...(subtitulo ? [{ text: subtitulo, style: "slideSubtitle" }] : []),
        ],
        fillColor: PDF_COLORS.primary,
        margin: [16, 10, 16, 12],
        border: [false, false, false, false],
      },
    ]],
  },
  layout: "noBorders",
});

// Tabla con encabezado a color y zebra suave (misma paleta de marca).
const tablaLayout = {
  fillColor: (rowIndex) => (rowIndex === 0 ? PDF_COLORS.tableHeader : (rowIndex % 2 === 0 ? PDF_COLORS.tint : null)),
  hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0),
  hLineColor: () => PDF_COLORS.tableDivider,
  vLineWidth: () => 0,
  paddingTop: () => 5,
  paddingBottom: () => 5,
  paddingLeft: () => 6,
  paddingRight: () => 6,
};

const buildTable = ({ headers, rows, widths }) => ({
  table: {
    headerRows: 1,
    widths,
    body: [
      headers.map((h) => ({ text: h, style: "tituloTablaClaro" })),
      ...rows.map((r) => r.map((c) => ({ text: c ?? "", style: "textoTabla" }))),
    ],
  },
  layout: tablaLayout,
});

// Tarjeta de KPI para la portada (número grande + etiqueta).
const statTile = (label, value) => ({
  table: {
    widths: ["*"],
    body: [[
      {
        stack: [
          { text: value, style: "tileValue" },
          { text: label, style: "tileLabel" },
        ],
        fillColor: PDF_COLORS.tint,
        margin: [12, 10, 12, 10],
        border: [false, false, false, false],
      },
    ]],
  },
  layout: "noBorders",
});

// Acomoda tarjetas de KPI en una rejilla de "cols" columnas.
const tileGrid = (tiles, cols = 3) => {
  const rows = [];
  for (let i = 0; i < tiles.length; i += cols) {
    const fila = tiles.slice(i, i + cols);
    rows.push({
      columns: fila.map((t) => ({ width: `${Math.floor(100 / cols)}%`, margin: [0, 0, 6, 8], ...t })),
      columnGap: 10,
    });
  }
  return rows;
};

// ===== Portada (slide de título + KPIs) =====
const sectionPortada = (data, empresa) => {
  const totalVentas = data.totalRenglones || (data.insumos?.length || 0);
  const totalCobros = data.ingresos?.length || 0;

  const tiles = [
    statTile("Renglones de ventas", String(totalVentas)),
    statTile("Total ingresos", money(data.totalIngresos || 0)),
    statTile("Cobros", String(totalCobros)),
    statTile("Comodatos", String(data.totalComodatos || 0)),
    statTile("Gastos", money(data.totalGastos || 0)),
    statTile("Cuentas por Cobrar", String(data.cuentasPorCobrar?.length || 0)),
    statTile("Montaje", String(data.montaje?.length || 0)),
    statTile("Show Room", String(data.showRoom?.length || 0)),
    statTile("Almacén de Montaje", String(data.almacenDeMontaje?.length || 0)),
  ];

  return [
    PDFHeaderV2({
      empresa,
      tituloPrincipal: "Reporte de Taxidermia",
      tituloFolio: `${fmtDate(data.fechaInicial)} - ${fmtDate(data.fechaFinal)}`,
      regimenFiscal: false,
      tipoComprobante: false,
      meta: [{ label: "Generado", value: moment().locale("es").format("LLLL") }],
    }),
    { text: "Resumen ejecutivo", style: "seccionTitulo", margin: [0, 18, 0, 12] },
    ...tileGrid(tiles, 3),
    { text: " ", pageBreak: "after" },
  ];
};

// ===== Ventas: (Codigo Cliente, Cliente, Animal, Fecha, Subtotal, Total) =====
const sectionVentas = (data) => {
  const rows = (data.insumos || []).map((i) => {
    const animal = i.descripcion || i.insumoNombre || i.insumo || "";
    const fecha = i.fechaVenta || i.fechaFactura || i.fechaRemision || "";

    const cantidad = Number(i.cantidad || 0);
    const pu = Number(i.precioUnitario || 0);
    const descPct = Number(i.descuento || 0);
    const subtotalCalc = (pu * cantidad) * (1 - (descPct / 100));
    const total = Number(i.total || subtotalCalc || 0);

    return [
      i.codigoCliente || "",
      i.cliente || i.razonSocial || "",
      animal,
      fecha,
      money(subtotalCalc),
      money(total),
    ];
  });

  return [
    slideTitleBand("Ventas"),
    buildTable({
      headers: ["Código Cliente", "Cliente", "Animal", "Fecha", "Subtotal", "Total"],
      widths: [80, "*", "*", 70, 80, 80],
      rows,
    }),
    { text: `Renglones: ${data.totalRenglones || rows.length}`, style: "subheader", margin: [0, 8, 0, 0] },
    { text: " ", pageBreak: "after" },
  ];
};

// ===== Comodatos creados en el mes, con sus insumos =====
const sectionComodatos = (data) => {
  const rows = (data.comodatos || []).map((c) => ([
    c.folio || "",
    c.entidad || "",
    c.proyecto || "",
    c.fechaOrden || "",
    c.descripcion || c.codigo || "",
    String(c.cantidad || 0),
    String(c.recibido || 0),
  ]));

  return [
    slideTitleBand("Comodatos"),
    buildTable({
      headers: ["Folio", "Cliente / Proveedor", "Proyecto", "Fecha Orden", "Insumo", "Cantidad", "Recibido"],
      widths: [70, "*", "*", 70, "*", 55, 55],
      rows,
    }),
    { text: `Renglones: ${data.totalComodatos || rows.length}`, style: "subheader", margin: [0, 8, 0, 0] },
    { text: " ", pageBreak: "after" },
  ];
};

// ===== Cobros: (Codigo Cliente, Cliente, Monto, Fecha, Forma de Pago) =====
const sectionCobros = (data) => {
  const rows = (data.ingresos || []).map((p) => ([
    p.codigoCliente || "",
    p.cliente || "",
    money(p.monto || 0),
    p.fechaPago || "",
    p.formaDePago || "",
  ]));

  return [
    slideTitleBand("Cobros", `Total ingresos: ${money(data.totalIngresos || 0)} · Registros: ${rows.length}`),
    buildTable({
      headers: ["Código Cliente", "Cliente", "Monto", "Fecha", "Forma de Pago"],
      widths: [80, "*", 90, 70, 120],
      rows,
    }),
    { text: " ", pageBreak: "after" },
  ];
};

// ===== Gastos: facturas de proveedor con fecha en el mes =====
const sectionGastos = (data) => {
  const rows = (data.gastos || []).map((g) => ([
    g.folio || "",
    g.proveedor || "",
    g.proyecto || "",
    g.fechaFactura || "",
    g.fechaVencimiento || "",
    money(g.total || 0),
    g.estado || "",
  ]));

  return [
    slideTitleBand("Gastos", `Total gastos: ${money(data.totalGastos || 0)} · Registros: ${rows.length}`),
    buildTable({
      headers: ["Folio", "Proveedor", "Proyecto", "Fecha", "Vencimiento", "Total", "Estado"],
      widths: [60, "*", "*", 60, 70, 80, 70],
      rows,
    }),
    { text: " ", pageBreak: "after" },
  ];
};

// ===== Actividades (Montaje / Showroom / Almacén) =====
const sectionActividades = (data, titulo, arr, isLast = false) => {
  const rows = (arr || []).map((a) => {
    const numero = a.numero || a.folio || a.lote || a.noSerie || a._id?.toString?.().slice(-6) || "";
    const insumo = a.insumoNombre || a.insumo?.descripcion || "";
    const inicio = a.fechaInicio ? fmtDate(a.fechaInicio) : "";
    const fin = a.fechaFinal ? fmtDate(a.fechaFinal) : "";
    const empleados = a.empleadosNombre || "";
    const estado = estadoActividadTxt(a.estadoActividad);

    return [String(numero), insumo, inicio, fin, empleados, estado];
  });

  const section = [
    slideTitleBand(titulo),
    buildTable({
      headers: ["Animal (Número)", "Insumo", "Inicio", "Fin", "Empleados", "Estado"],
      widths: [90, "*", 70, 70, "*", 80],
      rows,
    }),
  ];

  if (!isLast) section.push({ text: " ", pageBreak: "after" });
  return section;
};

// ===== Cuentas por Cobrar: (Codigo Cliente, Cliente, Fecha, Fecha Vencimiento, Total, Abonos, Saldo) =====
const sectionCxc = (data) => {
  const rows = (data.cuentasPorCobrar || []).map((v) => ([
    v.codigoCliente || "",
    v.cliente || "",
    v.fechaFactura || "",
    v.fechaVencimiento || "",
    money(v.totalPagar || 0),
    money(v.totalPagado || 0),
    money(v.saldo || 0),
  ]));

  const t = data.cuentasPorCobrarTotales || {};

  return [
    slideTitleBand("Cuentas por Cobrar", `Saldo total: ${money(t.totalSaldo || 0)} · Registros: ${rows.length}`),
    {
      columns: [
        { text: `No vencido: ${money(t.totalNoVencido || 0)}`, style: "textoChicoData" },
        { text: `0-30: ${money(t.totalVencido30 || 0)}`, style: "textoChicoData" },
        { text: `30-60: ${money(t.totalVencido60 || 0)}`, style: "textoChicoData" },
        { text: `60-90: ${money(t.totalVencido90 || 0)}`, style: "textoChicoData" },
        { text: `90+: ${money(t.totalVencidoMas || 0)}`, style: "textoChicoData" },
      ],
      columnGap: 10,
      margin: [0, 0, 0, 8],
    },
    buildTable({
      headers: ["Código Cliente", "Cliente", "Fecha", "Fecha Venc.", "Total", "Abonos", "Saldo"],
      widths: [80, "*", 70, 80, 80, 80, 80],
      rows,
    }),
  ];
};

// ===== Main builder =====
const buildReporteTaxidermiaPdfMake = (data) => {
  // Respaldo defensivo: si el backend no pudo resolver la empresa (o su logo),
  // no debe tumbar la generación del PDF completo.
  const empresa = data?.empresa || {};
  applyPdfColors(empresa);

  const periodoLabel = `${fmtDate(data.fechaInicial)} - ${fmtDate(data.fechaFinal)}`;

  const content = [
    ...sectionPortada(data, empresa),
    ...sectionVentas(data),
    ...sectionComodatos(data),
    ...sectionCobros(data),
    ...sectionGastos(data),
    ...sectionActividades(data, "Montaje", data.montaje),
    ...sectionActividades(data, "Show Room", data.showRoom),
    ...sectionActividades(data, "Almacén de Montaje", data.almacenDeMontaje),
    ...sectionCxc(data),
  ];

  const docDefinition = {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: PAGE_MARGINS,

    // Encabezado de marca en todas las páginas excepto la portada.
    header: (currentPage, pageCount) => {
      if (currentPage === 1) return { text: "" };
      return {
        margin: [28, 14, 28, 0],
        columns: [
          { width: 30, image: "logo", fit: [30, 22] },
          {
            width: "*",
            margin: [10, 1, 0, 0],
            stack: [
              { text: "Reporte de Taxidermia", style: "header" },
              { text: periodoLabel, style: "subheader" },
            ],
          },
          {
            width: "auto",
            text: `Página ${currentPage} de ${pageCount}`,
            style: "textoChicoData",
            alignment: "right",
            margin: [0, 8, 0, 0],
          },
        ],
      };
    },

    // Pie de marca (BIWO) en todas las páginas excepto la portada.
    footer: (currentPage) => (currentPage === 1 ? { text: "" } : PDFBrandFooterV2()),

    images: { logo: empresa?.logo?.base64Completa || BLANK_PIXEL },

    defaultStyle: { font: MULISH_FONT },
    styles: {
      ...pdfStylesV2(empresa),
      slideTitle: { fontSize: pdfFz(20), bold: true, color: "#FFFFFF" },
      slideSubtitle: { fontSize: pdfFz(10), color: PDF_COLORS.tint },
      tituloTablaClaro: { fontSize: pdfFz(7), bold: true, color: "#FFFFFF", alignment: "center" },
      tileValue: { fontSize: pdfFz(16), bold: true, color: PDF_COLORS.primaryDark || PDF_COLORS.ink },
      tileLabel: { fontSize: pdfFz(7.5), color: PDF_COLORS.label },
    },

    content,
  };

  return docDefinition;
};

export default buildReporteTaxidermiaPdfMake;
